// @flow
import {buildShopUrl} from "../../helper/shopUrl";

/**
 * URL conventions of the IT-Tecture hosted variant (zubehoer-navigator.de):
 *
 *   /<language>/<brand>                         dealer finder of a brand
 *   /<language>/<brand>/<dealerId>              a dealer's shop (module in browser mode, this is its basename)
 *   /<language>/<brand>/<dealerId>/product/…    module routes below the basename
 *
 * Legacy links address the dealer as `?dealer=<id>` (optionally with a `#/route`); they are
 * redirected to the path form so every product has one canonical address (#56171).
 */

export type HostedPath = {
    language: ?string,
    brand: ?string,
    dealerId: ?string,
    route: string,
};

const segmentsOf = (pathname: string): string[] => (pathname || "").split("/").filter(Boolean);

export const parseHostedPath = (pathname: string): HostedPath => {
    const [language, brand, dealerId, ...rest] = segmentsOf(pathname);
    return {
        language: language || null,
        brand: brand || null,
        dealerId: dealerId || null,
        route: rest.length ? `/${rest.join("/")}` : "/",
    };
};

export const hostedBasename = (pathname: string): ?string => {
    const {language, brand, dealerId} = parseHostedPath(pathname);
    return language && brand && dealerId ? `/${language}/${brand}/${dealerId}` : null;
};

// path form first, the legacy query parameter as fallback
export const resolveHostedDealerId = (
    pathname: string = window.location.pathname,
    search: string = window.location.search
): ?string => parseHostedPath(pathname).dealerId || new URLSearchParams(search).get("dealer") || null;

export const hostedBrandHref = (pathname: string = window.location.pathname): string => {
    const {language, brand} = parseHostedPath(pathname);
    return `/${language || ""}/${brand || ""}`.replace(/\/+$/, "") || "/";
};

export const hostedDealerHref = (dealerId: string, pathname: string = window.location.pathname): string =>
    `${hostedBrandHref(pathname)}/${dealerId}`;

/**
 * The path-form address of a legacy `/<lang>/<brand>?dealer=<id>[#/route]` URL, or null when the
 * URL needs no redirect. Other query parameters (utm_*, country) are kept.
 */
export const legacyDealerRedirectTarget = (location: {pathname: string, search: string, hash: string}): ?string => {
    const {language, brand, dealerId} = parseHostedPath(location.pathname);
    if (!language || !brand || dealerId) {
        return null;
    }
    const params = new URLSearchParams(location.search);
    const dealer = params.get("dealer");
    if (!dealer) {
        return null;
    }
    params.delete("dealer");
    const route = location.hash.startsWith("#/") && location.hash !== "#/" ? location.hash.slice(1) : "";
    const search = params.toString() ? `?${params.toString()}` : "";
    return `/${language}/${brand}/${dealer}${route}${search}`;
};

/**
 * A dealer's own shop page for a brand, in the dealer's shop routing, carrying the visitor's
 * query parameters (utm_*, …) over to the dealer's site.
 */
export const dealerShopUrl = (shopBaseUrl: string, routing: ?string, brand: ?string, search: string = window.location.search): string => {
    const withProtocol = shopBaseUrl.includes("http") ? shopBaseUrl : `https://${shopBaseUrl}`;
    const target = new URL(withProtocol);
    new URLSearchParams(search).forEach((value, key) => target.searchParams.append(key, value));
    const route = brand ? `/brand/${brand}/shop`.toLowerCase() : "/shop";
    return buildShopUrl(target.href, routing, route);
};
